import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DoneAll, NotificationsActive, NotificationsNone } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as alertsApi from '@/api/alerts';
import type { AlertResponse, AlertSeverity, AlertStatus, AlertType } from '@/api/alerts';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/alerts/')({
  component: AlertsPage,
});

type StatusFilter = 'Active' | 'Resolved' | 'All';

const SEVERITY: Record<AlertSeverity, { label: string; color: 'error' | 'warning' | 'info' }> = {
  Critical: { label: 'Crítica', color: 'error' },
  Warning: { label: 'Atención', color: 'warning' },
  Info: { label: 'Info', color: 'info' },
};

const TYPE_LABEL: Record<AlertType, string> = {
  CertificateExpiring: 'Certificado por vencer',
  CertificateExpired: 'Certificado vencido',
  InvoiceRejected: 'Comprobante rechazado',
  UnpaidFees: 'Honorarios impagos',
  RoomWaiting: 'Sala esperando',
  PadronCategoryChanged: 'Cambio de categoría fiscal',
  FiscalDueDate: 'Vencimiento fiscal',
};

function AlertsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('Active');
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const statusParam: AlertStatus | undefined = filter === 'All' ? undefined : filter;

  const alertsQ = useQuery({
    queryKey: ['alerts', filter],
    queryFn: () => alertsApi.listAlerts(statusParam, 0, 100),
    refetchInterval: 60_000,
  });

  const resolveMut = useMutation({
    mutationFn: (id: string) => alertsApi.resolveAlert(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setToast({ message: 'Alerta resuelta.', severity: 'success' });
    },
    onError: (err) => setToast({ message: toApiError(err).message, severity: 'error' }),
  });

  const resolveAllMut = useMutation({
    mutationFn: () => alertsApi.resolveAllAlerts(),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      setToast({ message: `${res.resolved} alerta(s) resuelta(s).`, severity: 'success' });
    },
    onError: (err) => setToast({ message: toApiError(err).message, severity: 'error' }),
  });

  const items = alertsQ.data?.items ?? [];
  const hasActive = items.some((a) => a.status === 'Active');

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsActive color="primary" /> Centro de Alertas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lo que requiere tu atención: vencimientos fiscales, certificados, comprobantes, honorarios y salas esperando.
          </Typography>
        </Box>
        <Tooltip title={hasActive ? '' : 'No hay alertas activas'}>
          <span>
            <Button
              variant="outlined"
              startIcon={<DoneAll />}
              disabled={!hasActive || resolveAllMut.isPending}
              onClick={() => resolveAllMut.mutate()}
            >
              Resolver todas
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_, v: StatusFilter | null) => v && setFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="Active">Activas</ToggleButton>
        <ToggleButton value="Resolved">Resueltas</ToggleButton>
        <ToggleButton value="All">Todas</ToggleButton>
      </ToggleButtonGroup>

      {alertsQ.isLoading ? (
        <LoadingScreen message="Cargando alertas…" />
      ) : alertsQ.error ? (
        <MuiAlert severity="error">{toApiError(alertsQ.error).message}</MuiAlert>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<NotificationsNone fontSize="inherit" />}
          title={filter === 'Active' ? 'Sin alertas activas' : 'Sin alertas'}
          description={filter === 'Active' ? 'Estás al día — no hay nada que requiera tu atención.' : 'No hay alertas para este filtro.'}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Severidad</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Detalle</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell align="right">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((a) => (
                <AlertRow key={a.id} alert={a} onResolve={() => resolveMut.mutate(a.id)} resolving={resolveMut.isPending} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function AlertRow({
  alert,
  onResolve,
  resolving,
}: {
  alert: AlertResponse;
  onResolve: () => void;
  resolving: boolean;
}): JSX.Element {
  const sev = SEVERITY[alert.severity];
  return (
    <TableRow hover>
      <TableCell>
        <Chip size="small" label={sev.label} color={sev.color} variant="outlined" />
      </TableCell>
      <TableCell>{TYPE_LABEL[alert.type] ?? alert.type}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {alert.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {alert.message}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {formatDateTime(alert.createdAt)}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {alert.status === 'Active' ? (
          <Button size="small" onClick={onResolve} disabled={resolving} startIcon={resolving ? <CircularProgress size={14} /> : undefined}>
            Resolver
          </Button>
        ) : (
          <Chip size="small" label="Resuelta" variant="outlined" />
        )}
      </TableCell>
    </TableRow>
  );
}
